/**
 * District of Columbia State-Specific Diagnostic Questions
 * All questions reference actual DC regulations, forms, and requirements
 * 
 * Regulatory Body: DC Department of Health (DC Health)
 *                  Health Care Facilities Division
 * 
 * PRIMARY REGULATIONS:
 * - 22 DCMR Chapter 39: Home Care Agencies (sections 3900-3999)
 * - 22 DCMR Chapter 31: Licensing of Health Care Facilities (general)
 * - DC Code § 44-501 et seq.: Health-Care Facility Licensure Act
 * 
 * REGULATION STRUCTURE (22 DCMR Chapter 39):
 * - § 3900: General Provisions
 * - § 3901: Insurance
 * - § 3902: License Fees
 * - § 3903: Governing Body
 * - § 3904: Director
 * - § 3905: Policies and Procedures
 * - § 3906: Contractor Agreements
 * - § 3907: Personnel
 * - § 3908: Admissions
 * - § 3909: Discharges, Transfers and Referrals
 * - § 3910: Records Retention and Disposal
 * - § 3911: Clinical Records
 * - § 3912: Patient Rights and Responsibilities
 * - § 3913: Complaint Process
 * - § 3914: Patient Plan of Care
 * - § 3915: Home Health and Personal Care Aide Services
 * - § 3916: Skilled Services Generally
 * - § 3917-3926: Specific Service Requirements
 * - § 3999: Definitions
 * 
 * KEY STATUTES:
 * - DC Code § 44-501 et seq.: Health-Care and Community Residence Facility, Hospice and Home Care Licensure Act
 * - DC Code § 44-551 et seq.: Health-Care Facility Unlicensed Personnel Criminal Background Check Act
 * - DC Code § 7-1903: Adult Protective Services Reporting Requirements
 * 
 * TRAINING REQUIREMENTS (3915.2, 3915.6):
 * - 75 hours initial training (classroom + 16 hours supervised practical)
 * - 12 hours annual in-service
 * - CNA supplement: 12 hours for home health aide qualification
 * 
 * SUPERVISION REQUIREMENTS (3915.7):
 * - Skilled services: every 2 weeks on-site
 * - Non-skilled services: every 62 days on-site
 * 
 * RECORD RETENTION (3910.4):
 * - 5 years after patient discharge
 */

import type { DiagnosticQuestionConfig } from '../../../types/diagnostic';

export const DC_QUESTIONS: DiagnosticQuestionConfig[] = [
  // ============================================
  // SECTION A: LICENSING & OPERATIONS
  // ============================================
  {
    id: 'DC-A1',
    itemId: 'A1',
    stateCode: 'DC',
    title: 'DC Health Home Care Agency License Status',
    description: 'District of Columbia requires all home care agencies to be licensed under 22 DCMR Chapter 39',
    severity: 'high',
    regulationCode: '22 DCMR 3900.2',
    regulationName: 'Licensing',
    agencyName: 'DC Department of Health',
    agencyAbbr: 'DC Health',
    agencyPhone: '(202) 442-5955',
    agencyUrl: 'https://dchealth.dc.gov/service/home-care-agencies',
    questions: [
      {
        id: 'q1',
        question: 'Do you have a current, active Home Care Agency license from DC Health?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
        helpText: 'Home care agency licenses must be renewed annually per 22 DCMR Chapter 31.',
      },
      {
        id: 'q2',
        question: 'What is your license expiration date?',
        inputType: 'date',
        helpText: 'Found on your home care agency license certificate',
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'daysFuture', questionId: 'q2', value: 90, result: 'COMPLIANT' },
      { condition: 'daysFuture', questionId: 'q2', value: 45, result: 'AT_RISK' },
      { condition: 'daysFuture', questionId: 'q2', value: 0, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Your DC Home Care Agency license is current and active per 22 DCMR 3900.2.',
      atRisk: 'Your license expires within 90 days. DC requires renewal applications to be submitted before expiration per 22 DCMR Chapter 31. Submit your renewal application immediately.',
      nonCompliant: 'Operating a home care agency in DC without a valid license violates 22 DCMR 3900.2 and DC Code § 44-501. Contact DC Health immediately at (202) 442-5955.',
      unknown: 'Verify your home care agency license status with DC Health at (202) 442-5955.',
    },
    stateNotes: [
      'Home care agency licenses are renewed annually per 22 DCMR Chapter 31',
      'License fees based on annual patient census (3902.1): 1-150 patients: $400; 151-750: $700; 751-1250: $1,100; 1251+: $1,300',
      'Initial application fee: $600',
      'Must maintain DC operating office staffed 8 hours/day M-F (3900.5)',
      'Must post license in conspicuous place (3900.7)',
    ],
  },

  {
    id: 'DC-A2',
    itemId: 'A2',
    stateCode: 'DC',
    title: 'Director Qualifications (22 DCMR 3904)',
    description: 'DC requires a qualified director responsible for managing agency operations',
    severity: 'high',
    regulationCode: '22 DCMR 3904',
    regulationName: 'Director',
    agencyName: 'DC Department of Health',
    agencyAbbr: 'DC Health',
    agencyPhone: '(202) 442-5955',
    agencyUrl: 'https://dchealth.dc.gov/service/home-care-agencies',
    questions: [
      {
        id: 'q1',
        question: 'Does your agency have a designated director who meets DC qualification requirements?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
        helpText: 'Director must be: (a) licensed physician, (b) licensed RN, or (c) have training/experience in health services admin with 1+ year supervisory experience in home health (3904.2)',
      },
      {
        id: 'q2',
        question: 'Which qualification does your director meet?',
        inputType: 'select',
        options: [
          { value: 'physician', label: 'Licensed physician' },
          { value: 'rn', label: 'Licensed registered nurse' },
          { value: 'admin', label: 'Health services admin training with 1+ year home health supervisory experience' },
          { value: 'none', label: 'None of the above' },
        ],
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'includes', questionId: 'q2', value: ['physician', 'rn', 'admin'], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'none', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Director qualifications meet 22 DCMR 3904.2 requirements.',
      atRisk: 'Director qualifications should be verified against 22 DCMR 3904.2.',
      nonCompliant: 'Your agency lacks a qualified director per 22 DCMR 3904.2. This is a critical licensing requirement.',
      unknown: 'Verify director qualifications meet 22 DCMR 3904.2 requirements.',
    },
    stateNotes: [
      'Director responsible for managing operations, serving as liaison to governing body, employing qualified personnel, ensuring adequate training (3904.1)',
    ],
  },

  {
    id: 'DC-A3',
    itemId: 'A3',
    stateCode: 'DC',
    title: 'Governing Body Requirements (22 DCMR 3903)',
    description: 'DC requires a governing body responsible for agency operations',
    severity: 'high',
    regulationCode: '22 DCMR 3903',
    regulationName: 'Governing Body',
    agencyName: 'DC Department of Health',
    agencyAbbr: 'DC Health',
    agencyPhone: '(202) 442-5955',
    agencyUrl: 'https://dchealth.dc.gov/service/home-care-agencies',
    questions: [
      {
        id: 'q1',
        question: 'Does your agency have a governing body responsible for operations?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Which governing body functions are documented? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'bylaws', label: 'Established and adopted by-laws and policies (3903.2(a))' },
          { value: 'director', label: 'Designated qualified director (3903.2(b))' },
          { value: 'annual_review', label: 'Annual review and evaluation of policies (3903.2(c))' },
          { value: 'patient_feedback', label: 'Patient feedback evaluation (10% or 40 patients) (3903.2(c)(1))' },
          { value: 'complaint_review', label: 'Review of all complaints (3903.2(c)(2))' },
          { value: 'written_report', label: 'Written annual evaluation report (3903.2(c)(3))' },
        ],
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'countAtLeast', questionId: 'q2', value: 5, result: 'COMPLIANT' },
      { condition: 'countAtLeast', questionId: 'q2', value: 3, result: 'AT_RISK' },
    ],
    reportLanguage: {
      compliant: 'Governing body structure meets 22 DCMR 3903 requirements.',
      atRisk: 'Governing body functions may be incomplete. Review 22 DCMR 3903.2 requirements.',
      nonCompliant: 'No governing body designated. This violates 22 DCMR 3903.',
      unknown: 'Verify governing body requirements.',
    },
    stateNotes: [
      'Governing body must establish and adopt by-laws and policies (3903.2(a))',
      'Must conduct annual review with patient feedback from 10% or 40 patients (3903.2(c)(1))',
      'Written annual evaluation report required (3903.2(c)(3))',
    ],
  },

  {
    id: 'DC-A4',
    itemId: 'A4',
    stateCode: 'DC',
    title: 'Written Policies and Procedures (22 DCMR 3905)',
    description: 'DC requires specific written operational policies',
    severity: 'medium',
    regulationCode: '22 DCMR 3905',
    regulationName: 'Policies',
    agencyName: 'DC Department of Health',
    agencyAbbr: 'DC Health',
    agencyPhone: '(202) 442-5955',
    agencyUrl: 'https://dchealth.dc.gov/service/home-care-agencies',
    questions: [
      {
        id: 'q1',
        question: 'Which DC-required policies does your agency have documented? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'contractor', label: 'Contractor agreements (3905.2(a))' },
          { value: 'personnel', label: 'Personnel (3905.2(b))' },
          { value: 'admission', label: 'Admission and denials of admission (3905.2(c))' },
          { value: 'discharge', label: 'Discharges and referrals (3905.2(d))' },
          { value: 'records', label: 'Records retention and disposal (3905.2(e))' },
          { value: 'patient_rights', label: 'Patient rights and responsibilities (3905.2(f))' },
          { value: 'complaint', label: 'Complaint process (3905.2(g))' },
          { value: 'services', label: 'Provision of each service offered (3905.2(h))' },
          { value: 'infection', label: 'Infection control (3905.2(h))' },
          { value: 'incidents', label: 'Unusual incidents (3905.2(i))' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'countAtLeast', questionId: 'q1', value: 10, result: 'COMPLIANT' },
      { condition: 'countAtLeast', questionId: 'q1', value: 7, result: 'AT_RISK' },
      { condition: 'countLessThan', questionId: 'q1', value: 7, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Your policy manual meets 22 DCMR 3905.2 requirements.',
      atRisk: 'Your policy manual is missing required sections per 22 DCMR 3905.2.',
      nonCompliant: 'Multiple required policies are missing per 22 DCMR 3905.2. Immediately develop written policies for all required areas.',
      unknown: 'Review your policies against 22 DCMR 3905.2 requirements.',
    },
    stateNotes: [
      'Policies must be approved by governing body (3905.1)',
      'Policies must be readily available to staff at all times (3905.3)',
      'Policies must be available to patients upon request (3905.4)',
    ],
  },

  {
    id: 'DC-A5',
    itemId: 'A5',
    stateCode: 'DC',
    title: 'Insurance Requirements (22 DCMR 3901)',
    description: 'DC requires specific minimum insurance coverage',
    severity: 'high',
    regulationCode: '22 DCMR 3901',
    regulationName: 'Insurance',
    agencyName: 'DC Department of Health',
    agencyAbbr: 'DC Health',
    agencyPhone: '(202) 442-5955',
    agencyUrl: 'https://dchealth.dc.gov/service/home-care-agencies',
    questions: [
      {
        id: 'q1',
        question: 'What insurance coverage does your agency maintain? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'malpractice', label: 'Blanket malpractice insurance - $1,000,000 per incident (3901.1(a))' },
          { value: 'general', label: 'General liability - $1,000,000 per occurrence (3901.1(b))' },
          { value: 'product', label: 'Product liability insurance (if applicable) (3901.1(c))' },
          { value: 'workers_comp', label: 'Workers\' compensation insurance' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'What is your malpractice coverage amount per incident?',
        inputType: 'select',
        options: [
          { value: '1m_plus', label: '$1,000,000 or more (meets DC requirement)' },
          { value: 'below_1m', label: 'Below $1,000,000' },
          { value: 'unsure', label: 'Not sure' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'countAtLeast', questionId: 'q1', value: 2 },
        { condition: 'equals', questionId: 'q2', value: '1m_plus' },
      ], result: 'COMPLIANT' },
      { condition: 'includes', questionId: 'q2', value: ['below_1m', 'unsure'], result: 'AT_RISK' },
      { condition: 'countLessThan', questionId: 'q1', value: 2, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Your insurance coverage meets 22 DCMR 3901 requirements.',
      atRisk: 'Your insurance may not meet DC minimum requirements. DC requires $1M malpractice and $1M general liability per 22 DCMR 3901.1.',
      nonCompliant: 'Your insurance coverage does not meet 22 DCMR 3901 requirements. Contact DC Health at (202) 442-5955 for guidance.',
      unknown: 'Verify your insurance meets 22 DCMR 3901 requirements.',
    },
    stateNotes: [
      'Malpractice: minimum $1,000,000 per incident (3901.1(a))',
      'General liability: minimum $1,000,000 per occurrence covering property damage, bodily injury, libel, slander (3901.1(b))',
      'Product liability required when applicable (3901.1(c))',
    ],
  },

  {
    id: 'DC-A6',
    itemId: 'A6',
    stateCode: 'DC',
    title: 'DC Operating Office Requirements (22 DCMR 3900.5)',
    description: 'DC requires a staffed operating office within the District',
    severity: 'high',
    regulationCode: '22 DCMR 3900.5',
    regulationName: 'Operating Office',
    agencyName: 'DC Department of Health',
    agencyAbbr: 'DC Health',
    agencyPhone: '(202) 442-5955',
    agencyUrl: 'https://dchealth.dc.gov/service/home-care-agencies',
    questions: [
      {
        id: 'q1',
        question: 'Do you maintain an operating office within the District of Columbia?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
        helpText: '22 DCMR 3900.5 requires a DC operating office',
      },
      {
        id: 'q2',
        question: 'Is your DC office staffed at least 8 hours per day, Monday through Friday?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
      },
      {
        id: 'q3',
        question: 'Does your DC office contain patient records for all DC patients and agency policies?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        helpText: '3900.6 requires records to be maintained or producible within 24 hours',
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'yes' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
        { condition: 'equals', questionId: 'q3', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q2', value: 'no' },
        { condition: 'equals', questionId: 'q3', value: 'no' },
      ], result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q2', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'equals', questionId: 'q3', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Operating office meets 22 DCMR 3900.5 and 3900.6 requirements.',
      atRisk: 'Operating office requirements may not be fully met. Review 22 DCMR 3900.5-3900.6.',
      nonCompliant: 'No DC operating office exists. This violates 22 DCMR 3900.5.',
      unknown: 'Verify operating office requirements.',
    },
    stateNotes: [
      'DC office required, staffed minimum 8 hours/day M-F (3900.5)',
      'Records must be maintained or producible within 24 hours (3900.6)',
      'License must be posted in conspicuous place (3900.7)',
    ],
  },

  {
    id: 'DC-A7',
    itemId: 'A7',
    stateCode: 'DC',
    title: 'Services Provided (22 DCMR 3900.4)',
    description: 'DC requires licensed agencies to provide at least one specified service',
    severity: 'high',
    regulationCode: '22 DCMR 3900.4',
    regulationName: 'Services',
    agencyName: 'DC Department of Health',
    agencyAbbr: 'DC Health',
    agencyPhone: '(202) 442-5955',
    agencyUrl: 'https://dchealth.dc.gov/service/home-care-agencies',
    questions: [
      {
        id: 'q1',
        question: 'Which services does your agency provide? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'skilled_nursing', label: 'Skilled nursing (3900.4(a))' },
          { value: 'pt', label: 'Physical therapy (3900.4(b))' },
          { value: 'ot', label: 'Occupational therapy (3900.4(c))' },
          { value: 'speech', label: 'Speech language pathology (3900.4(d))' },
          { value: 'iv', label: 'Intravenous therapy (3900.4(e))' },
          { value: 'social', label: 'Medical social services (3900.4(f))' },
          { value: 'aide', label: 'Home health aide or personal care aide services (3900.4(g))' },
        ],
        required: true,
        helpText: 'Must provide at least one of these services per 22 DCMR 3900.4',
      },
    ],
    scoringRules: [
      { condition: 'countAtLeast', questionId: 'q1', value: 1, result: 'COMPLIANT' },
      { condition: 'countLessThan', questionId: 'q1', value: 1, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Your agency provides at least one service required for licensure per 22 DCMR 3900.4.',
      atRisk: 'Verify your services align with your license.',
      nonCompliant: 'No qualifying services identified. 22 DCMR 3900.4 requires at least one specified service.',
      unknown: 'Review service requirements per 22 DCMR 3900.4.',
    },
    stateNotes: [
      'Must provide at least one service listed in 3900.4(a)-(g)',
      'Services include: skilled nursing, PT, OT, SLP, IV therapy, medical social, HHA/PCA',
      'Agency must maintain clinical director when providing skilled nursing (3904.3)',
    ],
  },

  // ============================================
  // SECTION B: PERSONNEL FILES
  // ============================================
  {
    id: 'DC-B1',
    itemId: 'B1',
    stateCode: 'DC',
    title: 'Personnel File Requirements (22 DCMR 3907.2)',
    description: 'DC requires specific documents in each employee personnel file',
    severity: 'high',
    regulationCode: '22 DCMR 3907.2',
    regulationName: 'Personnel Records',
    agencyName: 'DC Department of Health',
    agencyAbbr: 'DC Health',
    agencyPhone: '(202) 442-5955',
    agencyUrl: 'https://dchealth.dc.gov/service/home-care-agencies',
    questions: [
      {
        id: 'q1',
        question: 'Which documents do you maintain in EVERY employee personnel file? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'name_address_ssn', label: 'Name, address, social security number (3907.2(a))' },
          { value: 'license', label: 'Current professional license/registration number (3907.2(b))' },
          { value: 'education', label: 'Resume/education/training certificates/skills checklist (3907.2(c))' },
          { value: 'cpr', label: 'Current CPR certification (3907.2(d))' },
          { value: 'health', label: 'Health certification (3907.2(e))' },
          { value: 'employment_verify', label: 'Verification of previous employment (3907.2(f))' },
          { value: 'references', label: 'Reference checks (3907.2(g))' },
          { value: 'evaluations', label: 'Completed annual evaluations (3907.2(h))' },
          { value: 'background', label: 'Criminal background check documentation (3907.2(i))' },
          { value: 'personnel_actions', label: 'Personnel actions (3907.2(j))' },
          { value: 'job_desc', label: 'Position description (3907.2(k))' },
          { value: 'competency', label: 'Competency testing results (3907.2(l))' },
          { value: 'hep_b', label: 'Hepatitis B vaccine acceptance/declination (3907.2(m))' },
          { value: 'liability', label: 'Liability insurance documentation (if applicable) (3907.2(n))' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'countAtLeast', questionId: 'q1', value: 12, result: 'COMPLIANT' },
      { condition: 'countAtLeast', questionId: 'q1', value: 8, result: 'AT_RISK' },
      { condition: 'countLessThan', questionId: 'q1', value: 8, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Personnel files contain all DC-required documentation per 22 DCMR 3907.2.',
      atRisk: 'Some personnel files are missing required documents per 22 DCMR 3907.2.',
      nonCompliant: 'Personnel files are missing critical documentation required by 22 DCMR 3907.2. DC Health surveyors will cite incomplete files.',
      unknown: 'Audit personnel files against 22 DCMR 3907.2 requirements.',
    },
    stateNotes: [
      'Personnel records for DC patients must be maintained in DC office or produced within 24 hours (3907.4)',
      'Each employee has right to review their personnel records (3907.5)',
    ],
  },

  {
    id: 'DC-B2',
    itemId: 'B2',
    stateCode: 'DC',
    title: 'Written Personnel Policies (22 DCMR 3907.1)',
    description: 'DC requires written personnel policies available to all staff',
    severity: 'medium',
    regulationCode: '22 DCMR 3907.1',
    regulationName: 'Personnel Policy',
    agencyName: 'DC Department of Health',
    agencyAbbr: 'DC Health',
    agencyPhone: '(202) 442-5955',
    agencyUrl: 'https://dchealth.dc.gov/service/home-care-agencies',
    questions: [
      {
        id: 'q1',
        question: 'Which personnel policies does your agency have documented? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'terms', label: 'Terms and conditions of employment (wages, hours, leave, insurance, benefits) (3907.1(a))' },
          { value: 'annual_eval', label: 'Annual performance evaluation provisions (3907.1(b))' },
          { value: 'probation', label: 'Probationary periods, promotions, discipline, termination, grievance (3907.1(c))' },
          { value: 'position_desc', label: 'Position description for each category of employee (3907.1(d))' },
          { value: 'orientation', label: 'Orientation, periodic training/CE, periodic competency evaluation (3907.1(e))' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'countAtLeast', questionId: 'q1', value: 5, result: 'COMPLIANT' },
      { condition: 'countAtLeast', questionId: 'q1', value: 3, result: 'AT_RISK' },
      { condition: 'countLessThan', questionId: 'q1', value: 3, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Personnel policies meet 22 DCMR 3907.1 requirements.',
      atRisk: 'Personnel policies may be incomplete. Review 22 DCMR 3907.1 requirements.',
      nonCompliant: 'Personnel policies are incomplete per 22 DCMR 3907.1.',
      unknown: 'Review personnel policy requirements.',
    },
    stateNotes: [
      '3907.1 lists five required personnel policy areas',
      'Must include orientation, training, and competency evaluation provisions (3907.1(e))',
      'Policies must be available to all staff',
    ],
  },

  {
    id: 'DC-B3',
    itemId: 'B3',
    stateCode: 'DC',
    title: 'Professional License Verification (22 DCMR 3907.9, 3907.10)',
    description: 'DC requires verification of all professional licenses',
    severity: 'high',
    regulationCode: '22 DCMR 3907.9',
    regulationName: 'License Verification',
    agencyName: 'DC Board of Nursing',
    agencyAbbr: 'DCBON',
    agencyPhone: '(202) 724-4900',
    agencyUrl: 'https://dchealth.dc.gov/bon',
    questions: [
      {
        id: 'q1',
        question: 'Do you verify professional licenses (RN, LPN, PT, etc.) before hiring?',
        inputType: 'select',
        options: [
          { value: 'always', label: 'Yes, always verified with DC licensing boards' },
          { value: 'sometimes', label: 'Sometimes verified' },
          { value: 'no', label: 'No verification process' },
        ],
        required: true,
        helpText: '3907.9 requires DC licensure; 3907.10 requires documentation of valid licenses',
      },
      {
        id: 'q2',
        question: 'Do you track license expiration dates and verify renewal?',
        inputType: 'select',
        options: [
          { value: 'system', label: 'Automated tracking system with alerts' },
          { value: 'manual', label: 'Manual tracking (spreadsheet, calendar)' },
          { value: 'none', label: 'No active tracking' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'always' },
        { condition: 'includes', questionId: 'q2', value: ['system', 'manual'] },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'sometimes', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Professional license verification process meets DC requirements.',
      atRisk: 'License verification is inconsistent. Implement systematic verification.',
      nonCompliant: 'Employing unlicensed professionals violates DC law and 22 DCMR 3907.9.',
      unknown: 'Review license verification processes.',
    },
    stateNotes: [
      '3907.9 requires DC licensure for all professionals',
      '3907.10 requires documentation of valid licenses',
      'DC Board of Nursing: (202) 724-4900 or dchealth.dc.gov/bon',
      'Track license expiration dates for timely verification',
    ],
  },

  {
    id: 'DC-B4',
    itemId: 'B4',
    stateCode: 'DC',
    title: 'Health Screening Requirements (22 DCMR 3907.6, 3907.7)',
    description: 'DC requires health screening for communicable disease',
    severity: 'high',
    regulationCode: '22 DCMR 3907.6',
    regulationName: 'Health Certification',
    agencyName: 'DC Department of Health',
    agencyAbbr: 'DC Health',
    agencyPhone: '(202) 442-5955',
    agencyUrl: 'https://dchealth.dc.gov/service/home-care-agencies',
    questions: [
      {
        id: 'q1',
        question: 'Do you verify at hire that employees have been screened for communicable disease within the preceding 6 months?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
        helpText: '22 DCMR 3907.6 requires verification within 6 months of hire',
      },
      {
        id: 'q2',
        question: 'Do employees receive annual communicable disease screening per CDC guidelines?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        helpText: '22 DCMR 3907.7 requires annual screening per CDC guidelines',
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'yes' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'no' },
        { condition: 'equals', questionId: 'q2', value: 'no' },
      ], result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Health screening meets 22 DCMR 3907.6 and 3907.7 requirements.',
      atRisk: 'Health screening may not meet DC requirements. Review 22 DCMR 3907.6-3907.7.',
      nonCompliant: 'Health screening is not conducted as required by 22 DCMR 3907.6-3907.7.',
      unknown: 'Verify health screening procedures.',
    },
    stateNotes: [
      'Initial screening within 6 months before hire (3907.6)',
      'Annual screening per CDC guidelines (3907.7)',
      'Employee with communicable disease posing health risk cannot provide services (3907.8(b))',
    ],
  },

  // ============================================
  // SECTION C: BACKGROUND SCREENING
  // ============================================
  {
    id: 'DC-C1',
    itemId: 'C1',
    stateCode: 'DC',
    title: 'Criminal Background Check (DC Code § 44-551)',
    description: 'DC requires criminal background checks per the Health-Care Facility Unlicensed Personnel Criminal Background Check Act',
    severity: 'high',
    regulationCode: 'DC Code 44-551',
    regulationName: 'Background Check',
    agencyName: 'DC Department of Health',
    agencyAbbr: 'DC Health',
    agencyPhone: '(202) 442-5955',
    agencyUrl: 'https://dchealth.dc.gov/',
    questions: [
      {
        id: 'q1',
        question: 'For ALL currently employed direct care staff, which background checks do you have on file? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'dc_check', label: 'DC criminal background check' },
          { value: 'fbi', label: 'FBI national fingerprint-based criminal check' },
          { value: 'nurse_aide_registry', label: 'DC Nurse Aide Registry check' },
          { value: 'sex_offender', label: 'Sex Offender Registry check' },
          { value: 'oig', label: 'OIG Exclusion List verification' },
        ],
        required: true,
        helpText: 'DC Code § 44-551 requires background checks for unlicensed personnel; licensed professionals must comply with DC Code § 3-1205.22',
      },
      {
        id: 'q2',
        question: 'Are background checks completed BEFORE employees have any patient contact?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes - always before patient contact' },
          { value: 'no', label: 'No - sometimes they start early' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      // NON_COMPLIANT if employees start before background check clearance (direct violation)
      { condition: 'equals', questionId: 'q2', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'countLessThan', questionId: 'q1', value: 3, result: 'NON_COMPLIANT' },
      { condition: 'allTrue', value: [
        { condition: 'countAtLeast', questionId: 'q1', value: 4 },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'countAtLeast', questionId: 'q1', value: 3, result: 'AT_RISK' },
    ],
    reportLanguage: {
      compliant: 'Background check documentation is complete per DC Code § 44-551 and 22 DCMR 3907.3.',
      atRisk: 'Background check documentation may be incomplete. Ensure all required checks are on file.',
      nonCompliant: 'VIOLATION: Employees working before background check clearance OR staff working without proper background checks. Per DC Code § 44-551, NO patient contact is allowed until background check is completed.',
      unknown: 'Audit all employee files for background check documentation.',
    },
    stateNotes: [
      '22 DCMR 3907.3 requires compliance with DC Code § 44-551 (Unlicensed Personnel Criminal Background Check Act)',
      'Licensed professionals must comply with DC Code § 3-1205.22 (HORA background check requirements)',
    ],
  },

  {
    id: 'DC-C2',
    itemId: 'C2',
    stateCode: 'DC',
    title: 'OIG Exclusion List Verification',
    description: 'Federal requirement to check OIG List of Excluded Individuals/Entities',
    severity: 'high',
    regulationCode: '42 CFR 1001',
    regulationName: 'OIG Exclusion',
    agencyName: 'Office of Inspector General',
    agencyAbbr: 'OIG',
    agencyPhone: '(800) 447-8477',
    agencyUrl: 'https://oig.hhs.gov/exclusions/',
    questions: [
      {
        id: 'q1',
        question: 'Do you check the OIG exclusion list for all employees?',
        inputType: 'select',
        options: [
          { value: 'always', label: 'Yes, at hire and monthly' },
          { value: 'hire_only', label: 'Yes, at hire only' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'always', result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'hire_only', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'OIG exclusion list verification is conducted at hire and monthly.',
      atRisk: 'OIG exclusion list should be checked monthly, not just at hire.',
      nonCompliant: 'Failure to check OIG exclusion list can result in severe penalties.',
      unknown: 'Verify OIG exclusion list check procedures.',
    },
    stateNotes: [
      'OIG LEIE check required under 42 CFR 1001',
      'Check at exclusions.oig.hhs.gov',
      'Monthly screening is federal best practice',
      'Billing for excluded individuals is Medicare/Medicaid fraud',
    ],
  },

  // ============================================
  // SECTION D: HEALTH & SAFETY
  // ============================================
  {
    id: 'DC-D1',
    itemId: 'D1',
    stateCode: 'DC',
    title: 'Employee Health Restrictions (22 DCMR 3907.8)',
    description: 'DC prohibits employees under certain conditions from providing services',
    severity: 'high',
    regulationCode: '22 DCMR 3907.8',
    regulationName: 'Health Restrictions',
    agencyName: 'DC Department of Health',
    agencyAbbr: 'DC Health',
    agencyPhone: '(202) 442-5955',
    agencyUrl: 'https://dchealth.dc.gov/',
    questions: [
      {
        id: 'q1',
        question: 'Do you have policies preventing employees from providing services if they are:',
        inputType: 'checklist',
        options: [
          { value: 'intoxicated', label: 'Under the influence of alcohol or mind-altering drugs (3907.8(a))' },
          { value: 'communicable', label: 'Have a communicable disease posing confirmed health risk (3907.8(b))' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'countAtLeast', questionId: 'q1', value: 2, result: 'COMPLIANT' },
      { condition: 'countAtLeast', questionId: 'q1', value: 1, result: 'AT_RISK' },
      { condition: 'countLessThan', questionId: 'q1', value: 1, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Employee health restriction policies meet 22 DCMR 3907.8 requirements.',
      atRisk: 'Employee health restriction policies may be incomplete.',
      nonCompliant: 'No employee health restriction policies exist per 22 DCMR 3907.8.',
      unknown: 'Review employee health restriction policies.',
    },
    stateNotes: [
      '3907.8(a): No services if under influence of alcohol or mind-altering drugs',
      '3907.8(b): No services if communicable disease poses confirmed health risk',
      'Document any work restrictions in personnel file',
    ],
  },

  {
    id: 'DC-D2',
    itemId: 'D2',
    stateCode: 'DC',
    title: 'Hepatitis B Vaccination Program (22 DCMR 3907.2(m))',
    description: 'DC requires documentation of Hepatitis B vaccination acceptance or declination',
    severity: 'medium',
    regulationCode: '29 CFR 1910.1030',
    regulationName: 'Hepatitis B',
    agencyName: 'OSHA / DC Health',
    agencyAbbr: 'OSHA',
    agencyPhone: '(800) 321-6742',
    agencyUrl: 'https://www.osha.gov/bloodborne-pathogens',
    questions: [
      {
        id: 'q1',
        question: 'Do you offer Hepatitis B vaccination to employees with occupational exposure risk?',
        inputType: 'select',
        options: [
          { value: 'yes', label: 'Yes, offered to all at-risk employees' },
          { value: 'some', label: 'Offered to some employees' },
          { value: 'no', label: 'No vaccination program' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Do you document acceptance or declination of the Hepatitis B vaccine?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        helpText: '22 DCMR 3907.2(m) requires documentation of acceptance or declination',
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'yes' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'some', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Hepatitis B vaccination program meets OSHA and DC requirements.',
      atRisk: 'Hepatitis B vaccination should be offered to all at-risk employees.',
      nonCompliant: 'OSHA requires Hepatitis B vaccination offer. DC requires documentation per 22 DCMR 3907.2(m).',
      unknown: 'Review Hepatitis B vaccination program.',
    },
    stateNotes: [
      '3907.2(m) requires documentation of Hepatitis B acceptance or declination',
      'OSHA 29 CFR 1910.1030 requires vaccination offer for occupational exposure',
      'Employees may decline but must sign declination form',
    ],
  },

  {
    id: 'DC-D3',
    itemId: 'D3',
    stateCode: 'DC',
    title: 'Infection Control Program (22 DCMR 3905.2(h))',
    description: 'DC requires infection control policies',
    severity: 'high',
    regulationCode: '22 DCMR 3905.2',
    regulationName: 'Infection Control',
    agencyName: 'DC Department of Health',
    agencyAbbr: 'DC Health',
    agencyPhone: '(202) 442-5955',
    agencyUrl: 'https://dchealth.dc.gov/',
    questions: [
      {
        id: 'q1',
        question: 'Does your agency have a written infection control program that includes:',
        inputType: 'checklist',
        options: [
          { value: 'policies', label: 'Written infection control policies and procedures' },
          { value: 'training', label: 'Staff training on infection prevention' },
          { value: 'ppe', label: 'PPE availability and proper use protocols' },
          { value: 'hand_hygiene', label: 'Hand hygiene protocols' },
          { value: 'surveillance', label: 'Infection surveillance and reporting' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'countAtLeast', questionId: 'q1', value: 5, result: 'COMPLIANT' },
      { condition: 'countAtLeast', questionId: 'q1', value: 3, result: 'AT_RISK' },
      { condition: 'countLessThan', questionId: 'q1', value: 3, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Infection control program meets 22 DCMR 3905.2(h) requirements.',
      atRisk: 'Infection control program may be incomplete.',
      nonCompliant: 'Infection control program does not meet 22 DCMR 3905.2(h) requirements.',
      unknown: 'Review infection control program.',
    },
    stateNotes: [
      '3905.2(h) requires infection control policies',
      'Must include written policies, staff training, PPE protocols',
      'Hand hygiene and infection surveillance essential',
    ],
  },

  // ============================================
  // SECTION E: TRAINING & COMPETENCY
  // ============================================
  {
    id: 'DC-E1',
    itemId: 'E1',
    stateCode: 'DC',
    title: 'Home Health Aide Training - 75 Hours (22 DCMR 3915.2)',
    description: 'DC requires 75 hours of training for home health aides and personal care aides',
    severity: 'high',
    regulationCode: '22 DCMR 3915.2',
    regulationName: 'Aide Training',
    agencyName: 'DC Department of Health',
    agencyAbbr: 'DC Health',
    agencyPhone: '(202) 442-5955',
    agencyUrl: 'https://dchealth.dc.gov/',
    questions: [
      {
        id: 'q1',
        question: 'Do ALL your home health aides/personal care aides have at least 75 hours of training documented?',
        inputType: 'select',
        options: [
          { value: 'all', label: 'Yes, all have 75+ hours documented' },
          { value: 'most', label: 'Most have it, some are in progress' },
          { value: 'no', label: 'No / Not tracking training hours' },
        ],
        required: true,
        helpText: '3915.2 requires 75 hours classroom and supervised practical training',
      },
      {
        id: 'q2',
        question: 'Does your training include at least 16 hours of supervised practical training?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        helpText: '3915.2 requires at least 16 hours supervised practical; 3915.3 requires 16 hours classroom before practical',
      },
      {
        id: 'q3',
        question: 'Is training provided by an approved program?',
        inputType: 'select',
        options: [
          { value: 'dc_approved', label: 'DC-approved program' },
          { value: 'community_college', label: 'Community college' },
          { value: 'national_foundation', label: 'National Foundation for Hospice and Home Care approved' },
          { value: 'other_state', label: 'Approved by another state' },
          { value: 'unsure', label: 'Not sure' },
        ],
        helpText: '3915.5 specifies approved training sources',
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'all' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'most', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'equals', questionId: 'q3', value: 'unsure', result: 'AT_RISK' },
    ],
    reportLanguage: {
      compliant: 'Home health aide training meets 75-hour requirement per 22 DCMR 3915.2.',
      atRisk: 'Some aides may not have completed 75 hours or practical training requirements.',
      nonCompliant: 'Aides are working without required 75 hours of training. This violates 22 DCMR 3915.2.',
      unknown: 'Review training documentation.',
    },
    stateNotes: [
      '75 hours total: classroom + supervised practical (3915.2)',
      'Minimum 16 hours supervised practical training (3915.2)',
      '16 hours classroom must be completed before supervised practical (3915.3)',
      'CNAs qualify with 12-hour supplemental home health training (3915.4)',
    ],
  },

  {
    id: 'DC-E2',
    itemId: 'E2',
    stateCode: 'DC',
    title: 'CNA Supplemental Training (22 DCMR 3915.4)',
    description: 'DC allows CNAs to qualify as HHAs with 12-hour supplemental training',
    severity: 'medium',
    regulationCode: '22 DCMR 3915.4',
    regulationName: 'CNA Supplement',
    agencyName: 'DC Department of Health',
    agencyAbbr: 'DC Health',
    agencyPhone: '(202) 442-5955',
    agencyUrl: 'https://dchealth.dc.gov/',
    questions: [
      {
        id: 'q1',
        question: 'Do you employ Certified Nurse Aides (CNAs) as home health aides?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Have CNAs completed the 12-hour supplemental training covering required topics?',
        inputType: 'checklist',
        options: [
          { value: 'infection', label: 'Infection control in the home (3915.4(a))' },
          { value: 'meals', label: 'Meal preparation and special diets (3915.4(b))' },
          { value: 'safety', label: 'Home safety (3915.4(c))' },
          { value: 'duties', label: 'Duties of a home health aide (3915.4(d))' },
          { value: 'environment', label: 'Maintenance of clean, safe, healthy environment (3915.4(e))' },
          { value: 'documentation', label: 'Documentation of patient status and care rendered (3915.4(f))' },
        ],
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'COMPLIANT' },
      { condition: 'countAtLeast', questionId: 'q2', value: 6, result: 'COMPLIANT' },
      { condition: 'countAtLeast', questionId: 'q2', value: 4, result: 'AT_RISK' },
      { condition: 'countLessThan', questionId: 'q2', value: 4, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'CNA supplemental training meets 22 DCMR 3915.4 requirements.',
      atRisk: 'CNA supplemental training may not cover all required topics.',
      nonCompliant: 'CNAs lack required 12-hour supplemental training per 22 DCMR 3915.4.',
      unknown: 'Review CNA supplemental training.',
    },
    stateNotes: [
      '3915.4: CNAs qualify with 12-hour supplemental training',
      'Must cover six topics: infection control, meals, safety, duties, environment, documentation',
      'Alternative path to 75-hour requirement for certified nurse aides',
    ],
  },

  {
    id: 'DC-E3',
    itemId: 'E3',
    stateCode: 'DC',
    title: 'Annual In-Service Training - 12 Hours (22 DCMR 3915.6)',
    description: 'DC requires 12 hours of annual continuing education for aides',
    severity: 'high',
    regulationCode: '22 DCMR 3915.6',
    regulationName: 'Annual Training',
    agencyName: 'DC Department of Health',
    agencyAbbr: 'DC Health',
    agencyPhone: '(202) 442-5955',
    agencyUrl: 'https://dchealth.dc.gov/',
    questions: [
      {
        id: 'q1',
        question: 'Do ALL home health aides/personal care aides complete 12 hours of in-service training annually?',
        inputType: 'select',
        options: [
          { value: 'all', label: 'Yes, all complete 12+ hours annually' },
          { value: 'most', label: 'Most complete training, some are behind' },
          { value: 'no', label: 'No structured annual training program' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Does annual training include a component related to care of persons with disabilities?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        helpText: '3915.6 specifically requires disability care component',
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'all' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'most', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Annual in-service training meets 12-hour requirement per 22 DCMR 3915.6.',
      atRisk: 'Some aides may be behind on annual training requirements.',
      nonCompliant: 'No structured annual in-service training program exists. This violates 22 DCMR 3915.6.',
      unknown: 'Review annual in-service training program.',
    },
    stateNotes: [
      '12 hours annual in-service after first year of service (3915.6)',
      'Must include disability care component (3915.6)',
      'Training must help maintain or improve performance (3915.6)',
    ],
  },

  {
    id: 'DC-E4',
    itemId: 'E4',
    stateCode: 'DC',
    title: 'Abuse Recognition and Reporting Training (DC Code § 7-1903)',
    description: 'DC requires mandatory reporter training for licensed health professionals',
    severity: 'high',
    regulationCode: 'DC Code 7-1903',
    regulationName: 'Mandatory Reporting',
    agencyName: 'DC Department of Aging and Community Living',
    agencyAbbr: 'DACL',
    agencyPhone: '(202) 541-3950',
    agencyUrl: 'https://dacl.dc.gov/service/adult-protective-services-0',
    questions: [
      {
        id: 'q1',
        question: 'Do ALL your staff receive training on identifying and reporting suspected abuse, neglect, or exploitation?',
        inputType: 'select',
        options: [
          { value: 'orientation_annual', label: 'Yes, during orientation and annually' },
          { value: 'orientation_only', label: 'Yes, during orientation only' },
          { value: 'no', label: 'No formal training' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Do staff know how to report to DC Adult Protective Services?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes, reporting procedures are documented and trained' },
          { value: 'no', label: 'No / not sure' },
        ],
        helpText: 'DC Code § 7-1903 requires licensed health professionals to report immediately',
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'orientation_annual' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'orientation_only' },
        { condition: 'equals', questionId: 'q2', value: 'no' },
      ], result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Staff are properly trained on mandatory abuse reporting per DC Code § 7-1903.',
      atRisk: 'Abuse reporting training may be insufficient.',
      nonCompliant: 'Failure to train on mandatory reporting requirements. DC Code § 7-1912 provides penalties: misdemeanor, fine up to $300 for failure to report.',
      unknown: 'Verify abuse reporting training.',
    },
    stateNotes: [
      'DC Code § 7-1903(a)(1): Licensed health professionals are mandatory reporters',
      'DC Code § 7-1912(a)(1): Willful failure to report is misdemeanor, fine up to $300',
      'DC Code § 7-1912(a)(5): Health professionals who fail to report face unprofessional conduct charges',
    ],
  },

  // ============================================
  // SECTION F: CARE DELIVERY
  // ============================================
  {
    id: 'DC-F1',
    itemId: 'F1',
    stateCode: 'DC',
    title: 'Patient Plan of Care (22 DCMR 3914)',
    description: 'DC requires individualized written plans of care for each patient',
    severity: 'high',
    regulationCode: '22 DCMR 3914',
    regulationName: 'Care Plan',
    agencyName: 'DC Department of Health',
    agencyAbbr: 'DC Health',
    agencyPhone: '(202) 442-5955',
    agencyUrl: 'https://dchealth.dc.gov/',
    questions: [
      {
        id: 'q1',
        question: 'Do you develop a written plan of care with patient participation for EVERY patient?',
        inputType: 'select',
        options: [
          { value: 'always', label: 'Yes, always before first visit' },
          { value: 'usually', label: 'Usually, but sometimes services start first' },
          { value: 'no', label: 'No formal care plan process' },
        ],
        required: true,
        helpText: '3914.1 requires written plan developed with patient participation',
      },
      {
        id: 'q2',
        question: 'Do your plans of care include all required elements? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'physician_orders', label: 'Physician orders for skilled services (3914.3(a))' },
          { value: 'aprn_orders', label: 'APRN orders when appropriate (3914.3(b))' },
          { value: 'goals', label: 'Goals and expected outcomes (3914.3(c))' },
          { value: 'services', label: 'Services description with frequency, amount, duration (3914.3(d))' },
          { value: 'personnel', label: 'Personnel responsible for each service (3914.3(e))' },
          { value: 'reevaluation', label: 'Provisions for reevaluation, discharge planning, continuation (3914.3(f))' },
          { value: 'diagnosis', label: 'Physical assessment and diagnoses (3914.3(g))' },
          { value: 'prognosis', label: 'Prognosis including rehab potential (3914.3(h))' },
          { value: 'limitations', label: 'Activities permitted/precluded due to limitations (3914.3(i))' },
          { value: 'psychosocial', label: 'Psychosocial needs (3914.3(j))' },
          { value: 'safety', label: 'Safety measures (3914.3(k))' },
          { value: 'emergency', label: 'Emergency protocols and responsible employees (3914.3(l)(m))' },
        ],
      },
      {
        id: 'q3',
        question: 'Is the plan of care approved and signed by a physician within 30 days of start of care?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        helpText: '3914.4 requires physician signature within 30 days',
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'always' },
        { condition: 'countAtLeast', questionId: 'q2', value: 10 },
        { condition: 'equals', questionId: 'q3', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'usually', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'countLessThan', questionId: 'q1', value: 1, result: 'NON_COMPLIANT' },
      { condition: 'equals', questionId: 'q3', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Plans of care meet 22 DCMR 3914 requirements.',
      atRisk: 'Plans of care may be incomplete or delayed.',
      nonCompliant: 'Services being provided without written plans of care. This violates 22 DCMR 3914.',
      unknown: 'Review plan of care process.',
    },
    stateNotes: [
      'Physician must approve and sign within 30 days (3914.4)',
      'Personal care aide services only may be signed by APRN (3914.4)',
      'Telephone orders must be signed within 30 days (3914.4)',
      'Multiple skilled services require documented coordination (3914.5)',
    ],
  },

  {
    id: 'DC-F2',
    itemId: 'F2',
    stateCode: 'DC',
    title: 'RN Supervision of Aides (22 DCMR 3915.7)',
    description: 'DC requires on-site RN supervision of home health and personal care aides',
    severity: 'high',
    regulationCode: '22 DCMR 3915.7',
    regulationName: 'Aide Supervision',
    agencyName: 'DC Department of Health',
    agencyAbbr: 'DC Health',
    agencyPhone: '(202) 442-5955',
    agencyUrl: 'https://dchealth.dc.gov/',
    questions: [
      {
        id: 'q1',
        question: 'Are home health/personal care aides supervised by an RN or appropriate health professional?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'How often does on-site supervision occur for SKILLED services?',
        inputType: 'select',
        options: [
          { value: 'every_2_weeks', label: 'Every 2 weeks or more frequently (meets requirement)' },
          { value: 'monthly', label: 'Monthly' },
          { value: 'less', label: 'Less frequently' },
        ],
        helpText: '3915.7: Skilled services require on-site supervision every 2 weeks',
      },
      {
        id: 'q3',
        question: 'How often does on-site supervision occur for NON-SKILLED services?',
        inputType: 'select',
        options: [
          { value: 'every_62_days', label: 'Every 62 days or more frequently (meets requirement)' },
          { value: 'quarterly', label: 'Quarterly' },
          { value: 'less', label: 'Less frequently' },
        ],
        helpText: '3915.7: Non-skilled services require on-site supervision every 62 days',
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q2', value: 'every_2_weeks' },
        { condition: 'equals', questionId: 'q3', value: 'every_62_days' },
      ], result: 'COMPLIANT' },
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q2', value: 'monthly' },
        { condition: 'equals', questionId: 'q3', value: 'quarterly' },
      ], result: 'AT_RISK' },
    ],
    reportLanguage: {
      compliant: 'RN supervision meets 22 DCMR 3915.7 requirements.',
      atRisk: 'Supervision frequency may not meet DC requirements.',
      nonCompliant: 'Aides are not receiving required RN supervision per 22 DCMR 3915.7.',
      unknown: 'Review supervision procedures.',
    },
    stateNotes: [
      'Skilled services: on-site supervision every 2 weeks (3915.7)',
      'Non-skilled services: on-site supervision every 62 days (3915.7)',
      'Aide services must be explained to patient by RN or health professional (3915.8)',
    ],
  },

  {
    id: 'DC-F3',
    itemId: 'F3',
    stateCode: 'DC',
    title: 'Skilled Services Review (22 DCMR 3916)',
    description: 'DC requires review and evaluation of skilled services every 62 days',
    severity: 'medium',
    regulationCode: '22 DCMR 3916',
    regulationName: 'Skilled Services',
    agencyName: 'DC Department of Health',
    agencyAbbr: 'DC Health',
    agencyPhone: '(202) 442-5955',
    agencyUrl: 'https://dchealth.dc.gov/',
    questions: [
      {
        id: 'q1',
        question: 'Do you review and evaluate skilled services for each patient at least every 62 days?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
        helpText: '3916.1 requires 62-day review with summary to physician',
      },
      {
        id: 'q2',
        question: 'Is a summary report sent to the patient\'s physician?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'yes' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'no', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Skilled services review meets 22 DCMR 3916.1 requirements.',
      atRisk: 'Summary reports may not be sent to physicians.',
      nonCompliant: 'Skilled services are not reviewed every 62 days per 22 DCMR 3916.1.',
      unknown: 'Review skilled services evaluation schedule.',
    },
    stateNotes: [
      '3916.1 requires 62-day review of skilled services',
      'Summary report to physician required',
      'Must include patient status and progress toward goals',
    ],
  },

  // ============================================
  // SECTION G: DOCUMENTATION/ADMIN
  // ============================================
  {
    id: 'DC-G1',
    itemId: 'G1',
    stateCode: 'DC',
    title: 'Clinical Record Requirements (22 DCMR 3911)',
    description: 'DC requires comprehensive clinical record documentation',
    severity: 'high',
    regulationCode: '22 DCMR 3911',
    regulationName: 'Clinical Records',
    agencyName: 'DC Department of Health',
    agencyAbbr: 'DC Health',
    agencyPhone: '(202) 442-5955',
    agencyUrl: 'https://dchealth.dc.gov/',
    questions: [
      {
        id: 'q1',
        question: 'Do patient clinical records include all required elements? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'admission', label: 'Admission data (name, address, DOB, sex, case #, next of kin, etc.) (3911.2(a))' },
          { value: 'referral', label: 'Source of referral (3911.2(b))' },
          { value: 'assessment', label: 'Initial assessment and ongoing evaluation (3911.2(c))' },
          { value: 'care_plan', label: 'Plan of care for each service (3911.2(d))' },
          { value: 'physician_orders', label: 'Physician orders (3911.2(e))' },
          { value: 'allergies', label: 'History of sensitivities and allergies (3911.2(f))' },
          { value: 'medications', label: 'Medication sheet (3911.2(g))' },
          { value: 'notes', label: 'Clinical, progress, and summary notes signed and dated (3911.2(h))' },
          { value: 'supervision', label: 'Documentation of supervision of home care services (3911.2(i))' },
          { value: 'discharge', label: 'Discharge planning and summary (3911.2(j)(k))' },
          { value: 'coordination', label: 'Documentation of coordination of services (3911.2(l))' },
          { value: 'training', label: 'Documentation of patient/caregiver training and education (3911.2(s))' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'countAtLeast', questionId: 'q1', value: 11, result: 'COMPLIANT' },
      { condition: 'countAtLeast', questionId: 'q1', value: 8, result: 'AT_RISK' },
      { condition: 'countLessThan', questionId: 'q1', value: 8, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Clinical records contain all required elements per 22 DCMR 3911.2.',
      atRisk: 'Clinical records may be missing some required elements.',
      nonCompliant: 'Clinical records do not meet 22 DCMR 3911 requirements.',
      unknown: 'Review clinical record documentation.',
    },
    stateNotes: [
      '3911.2 lists 20+ required clinical record elements',
      'All notes must be signed and dated (3911.2(h))',
      'Must document patient/caregiver training (3911.2(s))',
      'Records must be maintained or producible within 24 hours (3900.6)',
    ],
  },

  {
    id: 'DC-G2',
    itemId: 'G2',
    stateCode: 'DC',
    title: 'Record Retention - 5 Years (22 DCMR 3910.4)',
    description: 'DC requires 5-year retention of patient records after discharge',
    severity: 'medium',
    regulationCode: '22 DCMR 3910',
    regulationName: 'Record Retention',
    agencyName: 'DC Department of Health',
    agencyAbbr: 'DC Health',
    agencyPhone: '(202) 442-5955',
    agencyUrl: 'https://dchealth.dc.gov/',
    questions: [
      {
        id: 'q1',
        question: 'How long do you retain patient clinical records after discharge?',
        inputType: 'select',
        options: [
          { value: '5plus', label: '5 years or longer (meets DC requirement)' },
          { value: '3to4', label: '3-4 years' },
          { value: 'less3', label: 'Less than 3 years' },
          { value: 'unsure', label: 'Not sure / no formal policy' },
        ],
        required: true,
        helpText: '22 DCMR 3910.4 requires 5 years after discharge',
      },
      {
        id: 'q2',
        question: 'Do you have written policies for record protection, confidentiality, retention, and storage?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        helpText: '3910.1(a) requires written policies',
      },
      {
        id: 'q3',
        question: 'Do you have procedures for disposition of records if the agency dissolves?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        helpText: '3910.1(b), 3910.2, 3910.3 address dissolution procedures',
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: '5plus' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
        { condition: 'equals', questionId: 'q3', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: '3to4', result: 'AT_RISK' },
      { condition: 'includes', questionId: 'q1', value: ['less3', 'unsure'], result: 'NON_COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'no', result: 'NON_COMPLIANT' },
      // No dissolution procedures per 3910.1(b), 3910.2, 3910.3
      { condition: 'equals', questionId: 'q3', value: 'no', result: 'AT_RISK' },
    ],
    reportLanguage: {
      compliant: 'Record retention meets DC\'s 5-year requirement under 22 DCMR 3910.4.',
      atRisk: 'DC requires 5-year record retention per 22 DCMR 3910.4 and dissolution procedures per 3910.1(b). Update your policy.',
      nonCompliant: 'Record retention does not meet DC\'s 5-year requirement.',
      unknown: 'Review record retention requirements.',
    },
    stateNotes: [
      '5 years after date of patient discharge (3910.4)',
      'If dissolved, records stored within 100 miles of DC or with patient\'s physician (3910.2)',
      'Must notify DC Health and patients within 30 days of dissolution (3910.3)',
    ],
  },

  {
    id: 'DC-G3',
    itemId: 'G3',
    stateCode: 'DC',
    title: 'Patient Rights and Responsibilities (22 DCMR 3912)',
    description: 'DC requires written patient rights be provided at admission',
    severity: 'high',
    regulationCode: '22 DCMR 3912',
    regulationName: 'Patient Rights',
    agencyName: 'DC Department of Health',
    agencyAbbr: 'DC Health',
    agencyPhone: '(202) 442-5955',
    agencyUrl: 'https://dchealth.dc.gov/',
    questions: [
      {
        id: 'q1',
        question: 'Do you provide EVERY patient with a written statement of rights upon admission?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
        helpText: '3912.1 requires written statement given to each patient at admission',
      },
      {
        id: 'q2',
        question: 'Does your patient rights statement include all DC-required elements? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'dignity', label: 'Right to courtesy, dignity, respect (3912.2(a))' },
          { value: 'control', label: 'Right to control household and lifestyle (3912.2(b))' },
          { value: 'informed', label: 'Right to be informed of services, costs, billing, etc. (3912.2(c))' },
          { value: 'treatment', label: 'Right to receive treatment consistent with plan of care (3912.2(d))' },
          { value: 'participate', label: 'Right to participate in planning care (3912.2(e))' },
          { value: 'competent', label: 'Right to competent personnel who can communicate (3912.2(f))' },
          { value: 'informed_condition', label: 'Right to be informed of condition (3912.2(g))' },
          { value: 'refuse', label: 'Right to refuse treatment and be informed of consequences (3912.2(h))' },
          { value: 'abuse_free', label: 'Right to be free from abuse, neglect, exploitation (3912.2(i))' },
          { value: 'confidential', label: 'Right to confidential handling of records (3912.2(j))' },
          { value: 'complaint', label: 'Right to voice complaints without fear of reprisal (3912.2(l))' },
          { value: 'access_records', label: 'Right to access own clinical records (3912.2(m))' },
          { value: 'hotline', label: 'DC Health Home Health Hotline number included (3912.2(c)(7))' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'countAtLeast', questionId: 'q2', value: 12, result: 'COMPLIANT' },
      { condition: 'countAtLeast', questionId: 'q2', value: 8, result: 'AT_RISK' },
    ],
    reportLanguage: {
      compliant: 'Patient rights documentation meets 22 DCMR 3912 requirements.',
      atRisk: 'Patient rights statement may be missing required elements.',
      nonCompliant: 'Patients are not receiving required rights information per 22 DCMR 3912.',
      unknown: 'Review patient rights process.',
    },
    stateNotes: [
      'Must inform patients of right to complain to DC Health (3912.3)',
      'Patient responsibilities also required (3912.4)',
      'Must be available to general public (3912.5)',
      'Must accommodate patients who cannot read or need alternative communication (3912.6)',
    ],
  },

  {
    id: 'DC-G4',
    itemId: 'G4',
    stateCode: 'DC',
    title: 'Complaint Process (22 DCMR 3913)',
    description: 'DC requires documented internal complaint process',
    severity: 'medium',
    regulationCode: '22 DCMR 3913',
    regulationName: 'Complaints',
    agencyName: 'DC Department of Health',
    agencyAbbr: 'DC Health',
    agencyPhone: '(202) 442-5955',
    agencyUrl: 'https://dchealth.dc.gov/',
    questions: [
      {
        id: 'q1',
        question: 'Do you have a documented internal complaint process?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Does your complaint process include the following? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'written_summary', label: 'Written summary given at acceptance/denial of services (3913.2(a))' },
          { value: 'given_to_current', label: 'Given to current patients (3913.2(b))' },
          { value: 'hotline_posted', label: 'DC Health Home Health Hotline posted in office (3913.3)' },
          { value: 'oral_written', label: 'Complaints may be oral or written (3913.4)' },
          { value: '14_day_response', label: 'Response within 14 calendar days (3913.5)' },
          { value: '30_day_written', label: 'Written response within 30 days if not satisfied (3913.6)' },
          { value: 'agency_contacts', label: 'Includes government agency contacts for complaints (3913.6)' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'countAtLeast', questionId: 'q2', value: 6, result: 'COMPLIANT' },
      { condition: 'countAtLeast', questionId: 'q2', value: 4, result: 'AT_RISK' },
    ],
    reportLanguage: {
      compliant: 'Complaint process meets 22 DCMR 3913 requirements.',
      atRisk: 'Complaint process may be incomplete.',
      nonCompliant: 'No complaint process per 22 DCMR 3913.',
      unknown: 'Review complaint procedures.',
    },
    stateNotes: [
      'Initial response within 14 calendar days (3913.5)',
      'Written response within 30 days if not satisfied (3913.6)',
      'DC Health Home Health Hotline must be posted in office (3913.3)',
    ],
  },

  {
    id: 'DC-G5',
    itemId: 'G5',
    stateCode: 'DC',
    title: 'Admission Policies (22 DCMR 3908)',
    description: 'DC requires written admission policies and initial assessment',
    severity: 'medium',
    regulationCode: '22 DCMR 3908',
    regulationName: 'Admissions',
    agencyName: 'DC Department of Health',
    agencyAbbr: 'DC Health',
    agencyPhone: '(202) 442-5955',
    agencyUrl: 'https://dchealth.dc.gov/',
    questions: [
      {
        id: 'q1',
        question: 'Does your admission policy include all required elements? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'criteria', label: 'Admission criteria and procedures (3908.1(a))' },
          { value: 'services', label: 'Description of services provided (3908.1(b))' },
          { value: 'charges', label: 'Amount charged for each service (3908.1(c))' },
          { value: 'fees', label: 'Policies on fees, payments, refunds (3908.1(d))' },
          { value: 'advance_directives', label: 'Advance directives (3908.1(e))' },
          { value: 'dnr', label: 'Do Not Resuscitate orders (3908.1(f))' },
          { value: 'consent', label: 'Consent for services (3908.1(g))' },
          { value: 'interagency', label: 'Consent for interagency sharing of information (3908.1(h))' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Do you conduct an initial assessment including a home visit?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        helpText: '3908.6 requires initial assessment including home visit',
      },
      {
        id: 'q3',
        question: 'Do you notify referring entities and individuals within 48 hours of availability/unavailability of service?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        helpText: '3908.4 requires 48-hour notification',
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'countAtLeast', questionId: 'q1', value: 7 },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'countAtLeast', questionId: 'q1', value: 5, result: 'AT_RISK' },
      { condition: 'countLessThan', questionId: 'q1', value: 5, result: 'NON_COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'equals', questionId: 'q3', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Admission policies meet 22 DCMR 3908 requirements.',
      atRisk: 'Admission policies may be incomplete.',
      nonCompliant: 'Admission policies do not meet 22 DCMR 3908 requirements.',
      unknown: 'Review admission policy requirements.',
    },
    stateNotes: [
      '3908.1 lists eight required admission policy elements',
      'Initial assessment must include home visit (3908.6)',
      'Must notify referring entities within 48 hours (3908.4)',
    ],
  },

  {
    id: 'DC-G6',
    itemId: 'G6',
    stateCode: 'DC',
    title: 'Contractor Agreements (22 DCMR 3906)',
    description: 'DC has specific requirements for contractor agreements',
    severity: 'medium',
    regulationCode: '22 DCMR 3906',
    regulationName: 'Contractors',
    agencyName: 'DC Department of Health',
    agencyAbbr: 'DC Health',
    agencyPhone: '(202) 442-5955',
    agencyUrl: 'https://dchealth.dc.gov/',
    questions: [
      {
        id: 'q1',
        question: 'If you use contractors, do you have written agreements that include all required elements?',
        inputType: 'select',
        options: [
          { value: 'yes', label: 'Yes, all contractors have written agreements with required elements' },
          { value: 'some', label: 'Some agreements, not all complete' },
          { value: 'no', label: 'No written agreements' },
          { value: 'na', label: 'N/A - no contractors used' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Do your contractor agreements include the following? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'services', label: 'Description of services to be provided (3906.1(a))' },
          { value: 'location', label: 'Location where services provided (3906.1(b))' },
          { value: 'control', label: 'Manner of control, coordination, evaluation (3906.1(c))' },
          { value: 'notes', label: 'Procedure for clinical notes, evaluations, scheduling (3906.1(d))' },
          { value: 'payment', label: 'Payment procedure and terms (3906.1(e))' },
          { value: 'monitoring', label: 'Procedures for managing/monitoring contract personnel (3906.1(f))' },
          { value: 'duration', label: 'Duration and renewal provisions (3906.1(g))' },
          { value: 'compliance', label: 'Assurance of compliance with agency policies, insurance, and laws (3906.1(h))' },
        ],
        showCondition: {
          questionId: 'q1',
          operator: 'notEquals',
          value: 'na',
        },
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'na', result: 'COMPLIANT' },
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'yes' },
        { condition: 'countAtLeast', questionId: 'q2', value: 7 },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'some', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Contractor agreements meet 22 DCMR 3906 requirements.',
      atRisk: 'Some contractor agreements may be incomplete.',
      nonCompliant: 'Contractor agreements do not meet 22 DCMR 3906 requirements.',
      unknown: 'Review contractor agreement requirements.',
    },
    stateNotes: [
      '3906.1 lists eight required contractor agreement elements',
      'Must include compliance assurance with agency policies and laws (3906.1(h))',
      'Contractors subject to same supervision requirements as employees',
    ],
  },

  {
    id: 'DC-G7',
    itemId: 'G7',
    stateCode: 'DC',
    title: 'Employee Identification (22 DCMR 3907.11)',
    description: 'DC requires employees to present valid agency identification',
    severity: 'medium',
    regulationCode: '22 DCMR 3907.11',
    regulationName: 'Employee ID',
    agencyName: 'DC Department of Health',
    agencyAbbr: 'DC Health',
    agencyPhone: '(202) 442-5955',
    agencyUrl: 'https://dchealth.dc.gov/',
    questions: [
      {
        id: 'q1',
        question: 'Do all employees/contractors present valid agency identification before entering a patient\'s home?',
        inputType: 'select',
        options: [
          { value: 'always', label: 'Yes, always - all staff have agency ID' },
          { value: 'most', label: 'Most do, not all have ID' },
          { value: 'no', label: 'No agency identification system' },
        ],
        required: true,
        helpText: '22 DCMR 3907.11 requires valid agency ID presentation',
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'always', result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'most', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Employee identification meets 22 DCMR 3907.11 requirements.',
      atRisk: 'Not all staff have agency identification.',
      nonCompliant: 'No agency identification system per 22 DCMR 3907.11.',
      unknown: 'Review employee identification requirements.',
    },
    stateNotes: [
      '3907.11 requires valid agency identification',
      'ID must be presented before entering patient home',
      'Include employee name and agency affiliation on ID',
    ],
  },
];

export default DC_QUESTIONS;